/**
 * @file        job/index.js
 * @author      Eddie Roosenmaallen, eddie@kingsds.network
 *              Matthew Palma, mpalma@kingsds.network
 *              Wes Garland, wes@kingsds.network
 *              Paul, paul@kingsds.network
 *              Ryan Saweczko, ryansaweczko@kingsds.network
 * @date        November 2018
 *              November 2018
 *              February 2022
 *              May 2022
 *              Jun 2022
 *
 * This module implements the Compute API's Job Handle
 * @copyright Copyright (c) 2018-2025, Distributive Corp. All Rights Reserved
 */
// @ts-check
'use strict';

const { BigNumber } = require('dcp/types');
const { EventEmitter, PropagatingEventEmitter } = require('dcp/common/dcp-events');
const { RangeObject, MultiRangeObject, DistributionRange, SuperRangeObject, SparseRangeObject } = require('dcp/dcp-client/range-object');
const { fetchURI, encodeDataURI, createTempFile, generateOpaqueId } = require('dcp/utils');
const { serializeJobValue, RemoteValue } = require('dcp/dcp-client/job-values');
const wallet = require('dcp/dcp-client/wallet');
const protocolV4 = require('dcp/protocol');
const { DcpURL } = require('dcp/common/dcp-url');
const ClientModal = require('dcp/dcp-client/dom-tk').modals;
const { DistributiveWorker } = require('dcp/dcp-client/worker');
const { RemoteDataSet } = require('dcp/dcp-client/remote-data-set');
const { RemoteDataPattern } = require('dcp/dcp-client/remote-data-pattern');
const { ResultHandle } = require('./result-handle');
const { fetchResults } = require('./download-slices');
const { SlicePaymentOffer } = require('./slice-payment-offer');
const { addSlices } = require('./upload-slices');
const dcpEnv = require('dcp/common/dcp-env');
const computeGroups = require('dcp/dcp-client/compute-groups');
const schedulerConstants = require('dcp/common/scheduler-constants');
const capabilities = require('dcp/common/capabilities');
const { sliceStatus, jobValueKind } = require('dcp/common/scheduler-constants');
const { jobStatus, skipStorage } = require('dcp/common/scheduler-constants');
const { DCPError } = require('dcp/common/dcp-error');
const { leafMerge } = require('dcp/utils/obj-merge');
const { setTimeout,
        clearTimeout,
        setInterval,
        clearInterval,
        Reference,
      } = require('dcp/common/dcp-timers');
const { requireNative } = require('dcp/dcp-client/webpack-native-bridge');

const debugging = require('dcp/debugging').scope('dcp-client');
const log = (...args) => {
  if (debugging('job')) {
    console.debug('dcp-client:job', ...args);
  }
};

const ON_BROWSER = dcpEnv.isBrowserPlatform;
const sideloaderModuleIdentifier = 'sideloader-v1';

/** @typedef {import('dcp/dcp-client/range-object').RangeLike} RangeLike */
/** @typedef {import('dcp/dcp-client/wallet/keystore').Keystore} Keystore */
/** @typedef {import('dcp/common/dcp-events').PropagatingEventEmitter} PropagatingEventEmitter */
/**
 * Ensure input data is an appropriate format
 * @param {RangeObject | DistributionRange | RemoteDataSet | Array | RangeLike}
 * inputData - A URI-shaped string, a [Multi]RangeObject-constructing value, or
 * an array of slice data
 * @return {RangeObject | DistributionRange | RemoteDataSet | Array | RangeLike}
 * The coerced input in an appropriate format ([Multi]RangeObject,
 * DistributionRange, RemoteDataSet, or array)
 */
 const wrangleData = (inputData) => {

  if (RangeObject.isRangelike(inputData)) { return inputData }
  if (RangeObject.isRangeObject(inputData)) { return inputData }
  if (DistributionRange.isDistribution(inputData)) { return inputData }
  if (inputData instanceof SparseRangeObject) { return inputData }
  if (inputData instanceof MultiRangeObject) { return inputData }
  if (MultiRangeObject.isProtoMultiRangelike(inputData)) { return new MultiRangeObject(inputData) }
  if (RangeObject.isProtoRangelike(inputData)) { return new RangeObject(inputData) }
  if (DistributionRange.isProtoDistribution(inputData)) { return new DistributionRange(inputData) }
  if (RemoteDataSet.isRemoteDataSet(inputData)) { return inputData }
  if (RemoteDataPattern.isRemoteDataPattern(inputData)) { return inputData }

  return Number.isInteger(inputData?.length) && inputData?.length >= 0 ? Array.from(inputData) : [inputData];
};

/**
 * @classdesc The Compute API's Job Handle (see {@link https://docs.dcp.dev/specs/compute-api.html#job-handles|Compute API spec})
 * Job handles are objects which correspond to jobs. 
 * They are created by some exports of the compute module, such as {@link module:dcp/compute.do|compute.do} and {@link module:dcp/compute.for|compute.for}.
 * @extends {PropagatingEventEmitter}
 * @hideconstructor
 * @access public
 */
class Job extends PropagatingEventEmitter
{
  /* Event loop references; jobDeployRef is held during the deploy operation; jobRef is held during exec. */
  #jobDeployRef = new Reference(debugging, () => `Job deploy reference: ${this.id || '(undeployed)'}`);
  #jobRef       = null;           /* null means "has never been referenced" */
  #autoClose;   /* Used to maintain the state of the job's autoClose prop, refer to `get autoClose` which is the public api */
  #KVIN;        /* KVIN instance used to marshal data for this job */

  /**
   * Fired when the job is accepted by the scheduler on deploy.
   * 
   * @event Job#accepted
   * @access public
   * @type {object}
   *//**
   * Fired when the job is cancelled.
   * 
   * @event Job#cancel
   * @access public
   *//**
   * Fired when a result is returned.
   * 
   * @event Job#result
   * @access public
   * @type {object}
   * @property {string} jobId Id of the job
   * @property {string} task ID of the task (slice) the result came from
   * @property {number} sort The index of the slice
   * @property {object} result
   * @property {string} result.request
   * @property {*} result.result The value returned from the work function
   *//**
   * Fired when the result handle is modified, either when a new `result` event is fired or when the results are populated with `results.fetch()`
   * 
   * @event Job#resultsUpdated
   * @access public
   *//**
   * Fired when the job has been completed.
   * 
   * @event Job#complete
   * @access public
   * @type {ResultHandle}
   *//**
   * Fired when the job's status changes.
   * 
   * @event Job#status
   * @access public
   * @type {object}
   * @property {string} jobId Id of the job
   * @property {number} total Total number of slices in the job
   * @property {number} distributed Number of slices that have been distributed
   * @property {number} computed Number of slices that have completed execution (returned a result)
   * @property {string} runStatus Current runStatus of the job
   *//**
   * Fired when a slice throws an error.
   * 
   * @event Job#error
   * @access public
   * @type {object}
   * @property {string} jobId Id of the job
   * @property {number} sliceIndex Index of the slice that threw the error
   * @property {string} message The error message
   * @property {string} stack The error stacktrace
   * @property {string} name The error type name
   *//**
   * Fired when a slice uses one of the console log functions.
   * 
   * @event Job#console
   * @access public
   * @type {object}
   * @property {string} jobId Id of the job
   * @property {number} sliceIndex The index of the slice that produced this event
   * @property {string} level The log level, one of `debug`, `info`, `log`, `warn`, or `error`
   * @property {string} message The console log message
   *//**
   * Fired when a slice is stopped for not calling progress. Contains information about how long the slice ran for, and about the last reported progress calls.
   * 
   * @event Job#noProgress
   * @access public
   * @type {object}
   * @property {string} jobId Id of the job
   * @property {number} sliceIndex The index of the slice that failed due to no progress
   * @property {number} timestamp How long the slice ran before failing
   * @property {object} progressReports
   * @property {object} progressReports.last The last progress report received from the worker
   * @property {number} progressReports.last.timestamp Time since the start of the slice
   * @property {number} progressReports.last.progress Progress value reported
   * @property {*} progressReports.last.value The last value that was passed to the progress function
   * @property {number} progressReports.last.throttledReports Number of calls to progress that were throttled since the last report
   * @property {object} progressReports.lastUpdate The last determinate (update to the progress param) progress report received from the worker
   * @property {number} progressReports.lastUpdate.timestamp
   * @property {number} progressReports.lastUpdate.progress
   * @property {*} progressReports.lastUpdate.value
   * @property {number} progressReports.lastUpdate.throttledReports
   *//**
   @todo: is this in the spec? is there a no progress data? should there be?
   * Identical to `noProgress`, except that it also contains the data that the slice was executed with.
   * 
   * @event Job#noProgressData
   * @access public
   * @type {object}
   * @property {*} data The data that the slice was executed with
   *//**
   * Fired when the job is paused due to running out of funds. The job can be resumed by escrowing more funds then resuming the job.
   * @todo: is this a thing, should it be a thing (the payload)
   * Event payload is the estimated funds required to complete the job
   * 
   * @event Job#nofunds
   * @access public
   * @type {BigNumber}
   *//**
   * Fired when the job cannot be deployed due to no bank account / not enough balance to deploy the job
   * 
   * @event Job#ENOFUNDS
   * @access public
   *//**
   * Fired when the job is cancelled due to the work function not calling the `progress` method frequently enough.
   * 
   * @event Job#ENOPROGRESS
   * @access public
   *//**
   * The job was cancelled because scheduler has determined that individual tasks in this job exceed the maximum allowable execution time.
   * 
   * @event Job#ESLICETOOSLOW
   * @access public
   *//**
   * Fired when the job is cancelled because too many work functions are terminating with uncaught exceptions.
   * 
   * @event Job#ETOOMANYERRORS
   * @access public
   */

  /**
   *  @form1     new Job('application_worker_id'[, data[, arguments]])
   *  @form2a    new Job('worker source'[, data[, arguments]])
   *  @form2b    new Job(worker_function[, data[, arguments]])
   */
  constructor ()
  {
    super('Job');

    if (typeof arguments[0] === 'function')
      arguments[0] = arguments[0].toString();

    if (typeof arguments[0] === 'string')
      this.workFunctionURI = encodeDataURI(arguments[0], 'application/javascript');
    else if (DcpURL.isURL(arguments[0]))
      this.workFunctionURI = arguments[0].href;

    this.jobInputData = wrangleData(arguments[1] || []);
    this.jobArguments = wrangleData(arguments[2] || []);
      
    log('num wrangledInputData:', this.jobInputData.length);
    log('num wrangledArguments:', this.jobArguments.length);

    this.initEventSystems();

    /**
     * An object describing the cost the user believes each the average slice will incur, in terms of CPU/GPU and I/O.
     * If defined, this object is used to provide initial scheduling hints and to calculate escrow amounts.
     *
     * @type {object}
     * @access public
     */
    this.initialSliceProfile = undefined;

    /**
     * An object describing the requirements that workers must have to be eligible for this job. The default values are set in the job-submitter, and only the client specified
     * requirements are sent over the wire. See {@link https://docs.dcp.dev/specs/compute-api.html#requirements-objects|Requirements Objects}.
     *
     * @type {object}
     * @access public
     */
    this.requirements = {};

    /**
     * A place to store public-facing attributes of the job. Anything stored on this object will be available inside the work 
     * function (see {@link module:dcp/compute~sandboxEnv.work}). The properties documented here may be used by workers to display what jobs are currently being 
     * worked on.
     * @access public
     * @property {string} name Public-facing name of this job.
     * @property {string} description Public-facing description for this job.
     * @property {string} link Public-facing link to external resource about this job.
     */
    this.public = {
      name: null,
      description: null,
      link: null,
    };
    
    /**
     * A cryptographic receipt indicating deployment of the job on the scheduler
     * @type {object}
     * @access public
     */
    this.receipt = null;
    
    /**
     * a SliceProfile object which contains the average costs for the slices which have been computed to date.
     * Until the first result is returned, this property is undefined.
     * @type {object}
     * @access public
     */
    this.meanSliceProfile = null;
    
    /**
     * A number (can be null, undefined, or infinity) describing the estimationSlicesRemaining in the jpd (dcp-2593)
     * @type {number}
     * @access public
     */
    this.estimationSlices = undefined;
    /**
     * When true, allows a job in estimation to have requestTask return multiple estimation slices.
     * This flag applies independent of infinite estimation, viz., this.estimationSlices === null .
     * @type {boolean}
     * @access public
     */
    this.greedyEstimation = false;
    /**
     * When true, a job will finish once it has completed execution of all slices.
     * Otherwise the job will be open to have additional slices added to it at any points in the future and compute them as it goes.
     * @type {boolean}
     */
    Object.defineProperty(this, 'autoClose', {
      get: () => this.#autoClose,
      set: (autoCloseValue) => {
        if (this.address)
          throw new Error('Cannot change autoClose status after dispatch');
        this.#autoClose = autoCloseValue;
      }
    });
    this.#autoClose = true;
    this.worktime = 'map-basic';
    this.worktimeVersion = undefined;
    this.customWorktime = false;
    this.#KVIN = new (require('kvin').KVIN)();
    /**
     * tunable parameters per job
     * @access public
     * @param {object} tuning     
     * @param {string} tuning.kvin     Encode the TypedArray into a string, trying multiple methods to determine optimum 
     *                                 size/performance.  The this.tune variable affects the behavior of this code this:
     * @param {boolean} speed          If true, only do naive encoding: floats get represented as byte-per-digit strings
     * @param {boolean} size           If true, try the naive, ab8, and ab16 encodings; pick the smallest
     *                                 If both are false try the naive encoding if under typedArrayPackThreshold and use if smaller
     *                                 than ab8; otherwise, use ab8
     */
    this.tuning = { kvin: {} };       /** this is not a good API. Find out who is using it and get them to stop! /wg Feb 2025 */
    Object.defineProperty(this.tuning.kvin, 'size', {
      get: () => this.#KVIN.tune === 'size',
      set: (b) => { this.#KVIN.tune = 'size' },
    });
    Object.defineProperty(this.tuning.kvin, 'speed', {
      get: () => this.#KVIN.tune === 'speed',
      set: (b) => { this.#KVIN.tune = 'speed' },
    });

    this.id             = generateOpaqueId();       /* id for the job */
    this.dependencies   = [];                       /* dependencies of the work function */
    this.requirePath    = [];                       /* require path for dependencies */
    this.modulePath     = [];                       /* path to module that invoked .exec() for job */
    this.connected      = false;                    /* true when exec or resume called */
    this.results        = new ResultHandle(this);   /* result handle */
    this.collateResults = true;                     /* option to receive results as they are computed & ensure all are received on finish */
    this.contextId      = null;                     /* optional string which is used to indicate to caching mechanisms different keystores with same name */ 
    this.workerConsole = false;                     /* tell workers to log more information about failures in the evaluator this job causes */
    this.address = null;                            /* job address, created by scheduler during exec call. */
    this.paymentAccountKeystore = null;             /* keystore for payment for job to come from */
    this.status = {                                 /* job status details */
      runStatus: null,
      total: null,
      distributed: null,
      computed: null
    };

    // service locations
    this.scheduler = dcpConfig.scheduler.services.jobSubmit.location;
    this.bank = dcpConfig.bank.services.bankTeller.location;

    // Compute groups. Add to public compute group by default
    this.computeGroups = [ Object.assign({}, schedulerConstants.computeGroups.public) ];

    // Update the ready state as we go through job deployment
    this.readyState = sliceStatus.new;
    const that = this;
    this.readyStateChange = function job$$readyStateChange (readyState)
    {
      that.readyState = readyState;
      that.emit('readyStateChange', that.readyState);
    }
  }

  /**
   * Initialize the various event systems the job handle requires. These include:
   *  - an internal event emitter (this.ee)
   *  - an event emitter for any events emitted on `work.emit` within work functions (this.work)
   *  - an event subscriber to subscribe (to receive) events from the scheduler (this.eventSubscriber)
   */
  initEventSystems ()
  {
    // Handle the various event-related things required in the constructor

    // Internal event emitter for events within job handle
    this.ee = new EventEmitter('Job Internal');

    /**
     * An EventEmitter for custom events dispatched by the work function.
     * @type {module:dcp/dcp-events.EventEmitter}
     * @access public
     * @example
     * // in work function
     * work.emit('myEventName', 1, [2], "three");
     * // client-side
     * job.work.on('myEventName', (num, arr, string) => { });
     */
    this.work = new EventEmitter('job.work');
    this.listenForCustomEvents = false;

    // Initialize the eventSubscriber so each job has unique eventSubscriber
    this.eventSubscriber = new (require('dcp/events/event-subscriber').EventSubscriber)(this);

    // Some events from the event subscriber can't be emitted immediately upon receipt without having 
    // weird/wrong output due to things like serialization. We allow interceptors in the event subscriber
    // to handle this.
    const that = this
    const parseConsole = function deserializeConsoleMessage(ev) {
      ev.message = that.#KVIN.unmarshal(ev.message);

      /* if we have the same message being logged (same sliceNumber, message, log level), the console event object will have the sole property same, nothing else */
      if (!(ev.same > 1))
        delete ev.same;
      else
      {
        const same = ev.same;
        Object.assign(ev, deserializeConsoleMessage.last);
        ev.same = same;
      }
      that.emit('console', ev);
      deserializeConsoleMessage.last = ev;
    }

    this.eventIntercepts = {
      result: (ev) => this.handleResult(ev),
      status: (ev) => this.handleStatus(ev),
      cancel: (ev) => this.ee.emit('stopped', ev),
      custom: (ev) => this.work.emit(ev.customEvent, ev),
      console: parseConsole,
    };
    
    this.eventTypes = require('dcp/common/dcp-events').eventTypes;

    this.work.on('newListener', (evt) => {
      this.listenForCustomEvents = true;
    });
    this.desiredEvents = []
    this.on('newListener', (evt) => {
      if (!this.connected && evt !== 'newListener')
        this.desiredEvents.push(evt);
      if (evt === 'cancel')
        this.listeningForCancel = true;
    });
  }
  
  /** 
   * Cancel the job
   * @access public
   * @param {string} reason If provided, will be sent to client
   */
  async cancel (reason = undefined)
  {
    const response = await this.useDeployConnection('cancelJob', {
      job: this.id,
      owner: this.paymentAccountKeystore.address,
      reason,
    }, this.paymentAccountKeystore);

    return response.payload;
  }

  /** 
   * Resume this job
   * @access public
   */
  async resume ()
  {
    // Careful -- this.schedulerConnection does not exist.
    const response = await this.schedulerConnection.request('resumeJob', {
      job: this.id,
      owner: this.paymentAccountKeystore.address,
    }, this.paymentAccountKeystore);

    return response.payload;
  }

  /**
   * Helper function for retrieving info about the job. The job must have already been deployed.
   * An alias for {@link module:dcp/compute.getJobInfo}.
   * @access public
   */
  getJobInfo ()
  {
    return require('../compute').compute.getJobInfo(this.id);
  }
  
  /**
   * create bundles for local dependencies
   */
  _pack ()
  {
    /* 'development' for source maps or 'production' for minified */
    const buildMode = 'production';

    return require('./node-modules').createModuleBundle(this.dependencies, buildMode);
  }

  /** 
   * Collect all of the dependencies together, throw them into a BravoJS
   * module which sideloads them as a side effect of declaration, and transmit
   * them to the package manager.  Then we return the package descriptor object,
   * which is guaranteed to have only one file in it.
   *
   * @returns   {Promise<object>}   with properties name and files[0]
   */
  async _publishLocalModules()
  {
    const dcpPublish = require('dcp/common/dcp-publish');
    
    const { tempFile, hash, unresolved } = await this._pack();

    if (!tempFile) {
      return { unresolved };
    }

    const sideloaderFilename = tempFile.filename;
    const pkg = {
      name: `dcp-pkg-v1-localhost-${hash.toString('hex')}`,
      version: '1.0.0',
      files: {
        [sideloaderFilename]: `${sideloaderModuleIdentifier}.js`,
      },
    }

    await dcpPublish.publish(pkg);
    tempFile.remove();

    return { pkg, unresolved };
  }
  
  /**
   * This function specifies a module dependency (when the argument is a string)
   * or a list of dependencies (when the argument is an array) of the work
   * function. This function can be invoked multiple times before deployment.
   * @param {string | string[]} modulePaths - A string or array describing one
   * or more dependencies of the job.
   * @access public
   */
  requires (modulePaths)
  {
    if (typeof modulePaths !== 'string' && (!Array.isArray(modulePaths) || modulePaths.some((modulePath) => typeof modulePath !== 'string')))
      throw new TypeError('The argument to dependencies is not a string or an array of strings');
    else if (modulePaths.length === 0)
      throw new RangeError('The argument to dependencies cannot be an empty string or array');
    else if (Array.isArray(modulePaths) && modulePaths.some((modulePath) => modulePath.length === 0))
      throw new RangeError('The argument to dependencies cannot be an array containing an empty string');

    if (!Array.isArray(modulePaths))
      modulePaths = [modulePaths];

    for (const modulePath of modulePaths)
    {
      if (modulePath[0] !== '.' && modulePath.indexOf('/') !== -1)
      {
        const modulePrefixRegEx = /^(.*)\/.*?$/;
        const [, modulePrefix] = modulePath.match(modulePrefixRegEx);
        if (modulePrefix && this.requirePath.indexOf(modulePrefix) === -1)
          this.requirePath.push(modulePrefix);
      }
      this.dependencies.push(modulePath);
    }
  }
  
  /** Set the account upon which funds will be drawn to pay for the job.
   *  @param {module:dcp/wallet.BankAccountKeystore} keystore A keystore that representa a bank account.
   *  @access public
   */
  setPaymentAccountKeystore (keystore)
  {
    if (this.address)
    {
      if (!keystore.address.eq(this.paymentAccountKeystore))
      {
        let message = 'Cannot change payment account after job has been deployed';
        this.emit('EPERM', message);
        throw new Error(`EPERM: ${message}`);
      }
    }
    
    if (!(keystore instanceof wallet.Keystore || keystore instanceof wallet.PrivateKey))
      throw new Error('Argument must be an instance of Keystore');
    this.paymentAccountKeystore = keystore;
  }
  
  /** Set the slice payment offer. This is equivalent to the first argument to exec.
   * @param {number} slicePaymentOffer - The number of DCC the user is willing to pay to compute one slice of this job
   */
  setSlicePaymentOffer (slicePaymentOffer)
  {
    this.slicePaymentOffer = new SlicePaymentOffer(slicePaymentOffer);
  }
  
  
  /**
   * @param {URL|DcpURL} locationUrl - A URL object
   * @param {object} postParams - An object with any parameters that a user would like to be passed to a 
   *                              remote result location. This object is capable of carry API keys for S3, 
   *                              DropBox, etc. These parameters are passed as parameters in an 
   *                              application/x-www-form-urlencoded request.
   */
  setResultStorage (locationUrl, postParams)
  {
    if (locationUrl instanceof URL || locationUrl instanceof DcpURL)
      this.resultStorageDetails = locationUrl;
    else
      this.resultStorageDetails = new URL(locationUrl);

    // resultStorageParams contains any post params required for off-prem storage
    if (typeof postParams !== 'undefined' && typeof postParams === 'object' )
      this.resultStorageParams = postParams;
    else
      throw new Error('postParams must be an object');

    // Some type of object here
    this.resultStorageType = 'pattern';
  }
  
  /**
   * This function is identical to exec, except that the job is executed locally
   * in the client.
   * @async
   * @param {number} cores - the number of local cores in which to execute the job.
   * @param {...any} args - The remaining arguments are identical to the arguments of exec
   * @return {Promise<ResultHandle>} - resolves with the results of the job, rejects on an error
   * @access public
   */
  async localExec (cores = 1, ...args)
  {
    if (!dcpEnv.isBrowserPlatform && dcpEnv.platform !== 'nodejs')
      throw new Error('localExec is not supported on this platform');

    const SandboxConstructor = dcpEnv.platform === 'nodejs'
          ? require('../worker/evaluators').nodeEvaluatorFactory()
          : require('../worker/evaluators').BrowserEvaluator;

    this.inLocalExec = true;
    this.setSlicePaymentOffer(0);
    this.paymentAccountKeystore = await new wallet.Keystore(null, '');
    this.estimationSlices = 0;
    this.greedyEstimation = false;

    this.localWorker = new DistributiveWorker(leafMerge(dcpConfig.worker, {
      paymentAddress:        wallet.nullAddress,
      allowConsoleAccess:    true,
      jobIds:                [],
      cores:                 { cpu: cores },
      maxSandboxes:          1,
      sandboxOptions: {
        ignoreNoProgress: true,
      },
      unmanaged: true,
    }), SandboxConstructor);
    this.localWorker.id = this.localWorker.id.replace(/(^)(.*)(..........$)/, 'localExec_$2'); /* important: _ not in randStrAlphabet */
    this.localWorker.runInfo.type = 'localexec';

    this.localWorker.on('error', (error) => {
      if (Boolean(dcpEnv.getenv('DCP_SUPERVISOR_DEBUG_DISPLAY_MAX_INFO')) || debugging())
        console.error(error);
      else
      {
        const errorCode = error?.errorCode ?? error?.code;
        if (errorCode)
          console.error(`LocalExec Error: ${error?.message}: error.code ${errorCode}`);
        else
          console.error(`LocalExec Error: ${error?.message}`);
      }
    });
    this.localWorker.on('warning', (warning) => {
      console.warn('LocalExec Warning:', warning);
    });
    
    this.on('accepted', () => {
      this.localWorker.config.jobIds.push(this.id);

      this.localWorker.start().catch((e) => {
        console.error('Failed to start worker for localExec:');
        console.error(e.message);
      });
    });

    if (dcpEnv.platform === 'nodejs')
    {
      // Determine type of input data
      // marshalInputData actually returns the union of dataRange, dataValues, dataPattern, sliceCount,
      // but there isn't a simple way to express that in Javascript.
      const { dataRange, dataValues } = marshalInputData(this.jobInputData);
      
      const inputSetFiles = [];
      
      let inputSetURIs = [];
      let dataSet;
      
      if (dataValues)
      {
        for (let i = 0; i < dataValues.length; i++)
        {
          if (!(dataValues[i] instanceof URL))
          {
            let marshaledInputValue = this.#KVIN.marshal(dataValues[i]);
            let inputDataFile = createTempFile('dcp-localExec-sliceData-XXXXXXXXX', 'kvin');
            inputDataFile.writeSync(JSON.stringify(marshaledInputValue));
            inputSetFiles.push(inputDataFile);
            inputSetURIs.push(new URL('file://' + inputDataFile.filename));
          }
          else
            inputSetURIs.push(dataValues[i]);
        }
        dataSet = new RemoteDataSet(inputSetURIs);
        if (dataSet.length > 0)
          this.marshaledDataValues = dataSet;
      }
      if (dataRange)
      {
        inputSetFiles.push(createTempFile('dcp-localExec-sliceData-XXXXXXXXX', 'json'));
        let marshaledInputSet = JSON.stringify(dataRange);
        inputSetFiles[0].writeSync(marshaledInputSet)
        inputSetURIs.push(new URL('file://' + inputSetFiles[0].filename));
        dataSet = new RemoteDataSet(inputSetURIs);
        this.marshaledDataRange = dataSet;
        this.rangeLength = dataRange.length;
      }
      
      // For allowed origins of the localexec worker. Only allow the origins (files in this case) in this list.
      for (let i = 0; i < inputSetFiles.length; i++)
      {
        // Coerce into a URI to handle OS specific path separators.
        const inputFileURI = new URL(`file://${inputSetFiles[i].filename}`);
        this.localWorker.originManager.add(inputFileURI.pathname, 'fetchData', null);
      }
        
      // Save work function to disk if work function starts with data (ie not remote)
      if (this.workFunctionURI.startsWith('data:'))
      {
        const workFunctionFile = createTempFile('dcp-localExec-workFunction-XXXXXXXXX', 'js');
        // data:URI - dont care about origins
        const workFunction = await fetchURI(this.workFunctionURI, false);
        workFunctionFile.writeSync(workFunction);
        
        const workFunctionFileURL = new URL('file://' + workFunctionFile);
        this.workFunctionURI = workFunctionFileURL.href;
        this.localWorker.originManager.add(workFunctionFileURL.pathname, 'fetchWorkFunctions', null);
      }
      
      let encodedJobArgumentUris = [];
      if (this.jobArguments)
      {
        if (this.jobArguments instanceof RemoteDataPattern) /* Not supported */
          throw new DCPError('Cannot use RemoteDataPattern as work function arguments', 'EBADARG')
        if (this.jobArguments instanceof RemoteDataSet) /* Entire set is RemoteDataSet */
        {
          this.jobArguments.forEach((e) =>
          {
            encodedJobArgumentUris.push(new URL(e));
          });
        }
        else
        {
          for (let i = 0; i < this.jobArguments.length; i++)
          {
            if (this.jobArguments[i] instanceof URL)
              encodedJobArgumentUris.push(this.jobArguments[i]);
            else
            {
              if (this.jobArguments[i] instanceof RemoteDataSet) /* Member of set is RemoteDataSet */
              {
                this.jobArguments[i].forEach((e) =>
                {
                  encodedJobArgumentUris.push(new URL(e));
                });
              }
              else /* Actual Value */
              {
                const localArgFile = createTempFile(`dcp-localExec-argument-${i}-XXXXXXXXX`, 'kvin');
                const localArgFileURI = new URL(`file://${localArgFile.filename}`);

                localArgFile.writeSync(this.#KVIN.stringify(this.jobArguments[i]));
                this.localWorker.originManager.add(localArgFileURI.pathname, 'fetchArguments', null);
                encodedJobArgumentUris.push(localArgFileURI);
              }
            }
          } 
        }
      }
      this.marshaledArguments = this.#KVIN.marshal(encodedJobArgumentUris);
    }
      
    return this.exec(...args).finally(() => {
      if (this.localWorker) {
        debugging() && console.debug('Waiting 3 seconds before shutting down worker.');
        const timerHandle = setTimeout(() => {
          // stop the worker
          this.localWorker.stop(true);
        }, 3000);
        // Allow workers and localExec to exit.
        timerHandle.unref();
      }
    });
  }

  /**
   * Deploys the job to the scheduler.
   * @param {number | object} [slicePaymentOffer=compute.marketValue] - Amount
   * in DCC that the user is willing to pay per slice.
   * @param {Keystore} [paymentAccountKeystore=wallet.get] - An instance of the
   * Wallet API Keystore that's used as the payment account when executing the
   * job.
   * @param {object} [initialSliceProfile] - An object describing the cost the
   * user believes the average slice will incur.
   * @access public
   * @emits Job#accepted
   */
  async exec (slicePaymentOffer = require('../compute').compute.marketValue, paymentAccountKeystore, initialSliceProfile)
  {
    if (this.connected)
      throw new Error(`Exec called twice on the same job handle (${this.id})`);
   
    if (this.estimationSlices === Infinity)
      this.estimationSlices = null;
    else if (this.estimationSlices < 0)
      throw new Error('Incorrect value for estimationSlices; it can be an integer or Infinity!');

    debugging() && this.#KVIN.tune && console.debug(`Slices and arguments are being uploaded with ${this.#KVIN.tune} optimization.`);

    this.readyStateChange('exec');
    if ((typeof slicePaymentOffer === 'number') || (typeof slicePaymentOffer === 'object')
      || ((this.slicePaymentOffer === null || this.slicePaymentOffer === undefined) && typeof slicePaymentOffer === 'function'))
      this.setSlicePaymentOffer(slicePaymentOffer);
    if (typeof initialSliceProfile !== 'undefined')
      this.initialSliceProfile = initialSliceProfile;
    
    if (typeof paymentAccountKeystore !== 'undefined')
    {
      /** XXX @todo deprecate use of ethereum wallet objects */
      if (typeof paymentAccountKeystore === 'object' && paymentAccountKeystore.hasOwnProperty('_privKey'))
      {
        console.warn('* deprecated API * - job.exec invoked with ethereum wallet object as paymentAccountKeystore') /* /wg oct 2019 */
        paymentAccountKeystore = paymentAccountKeystore._privKey
      }
      /** XXX @todo deprecate use of private keys */
      if (wallet.isPrivateKey(paymentAccountKeystore))
      {
        console.warn('* deprecated API * - job.exec invoked with private key as paymentAccountKeystore') /* /wg dec 2019 */
        paymentAccountKeystore = await new wallet.Keystore(paymentAccountKeystore, '');
      }

      this.setPaymentAccountKeystore(paymentAccountKeystore)
    }
    
    if (this.paymentAccountKeystore)
    {
      if (!this.inLocalExec)
        // Throws if they fail to unlock, we allow this since the keystore was set programmatically. 
        await this.paymentAccountKeystore.unlock(undefined, parseFloat(dcpConfig.job.maxDeployTime));
    }
    else
    {
      // If not set programmatically, we keep trying to get an unlocked keystore ... forever.
      let locked = true;
      let safety = 0; // no while loop shall go unguarded
      let ks;
      do
      {
        ks = null;
        // custom message for the browser modal to denote the purpose of keystore submission
        // let msg = `This application is requesting a keystore file to execute ${this.public.description || this.public.name || 'this job'}.<br><br> Please upload the corresponding keystore file. If you upload a keystore file which has been encrypted with a passphrase, the application will not be able to use it until it prompts for a passphrase and you enter it.`;
        let msg = `Upload a DCP bank account keystore<br> containing compute credits.`;
        ks = await wallet.get({ contextId: this.contextId, jobName: this.public.name, msg});
        if (ks)
        {
          try
          {
            await ks.unlock(undefined, parseFloat(dcpConfig.job.maxDeployTime));
            locked = false;
          }
          catch (e)
          {
            // prompt user again if user enters password incorrectly, exit modal otherwise
            if (e.code !== wallet.unlockFailErrorCode) throw e;
          }
        }
        if (safety++ > 1000) throw new Error('EINFINITY: job.exec tried wallet.get more than 1000 times.')
      } while (locked);
      this.setPaymentAccountKeystore(ks)
    }
    
    // We either have a valid keystore + password or we have rejected by this point.
    if (!this.slicePaymentOffer)
      throw new Error('A payment profile must be assigned before executing the job');
    else
      this.feeStructure = this.slicePaymentOffer.toFeeStructure(this.jobInputData.length);

    if (!this.address)
    {
      try
      {
        this.readyStateChange('init');
        await this.deployJob();
        const listenersPromise = this.addInitialEvents();
        const computeGroupsPromise = this.joinComputeGroups();
        let uploadSlicePromise;
        // if job data is by value then upload data to the scheduler in a staggered fashion
        if (Array.isArray(this.dataValues) && !this.marshaledDataValues)
        {
          this.readyStateChange('uploading');
          uploadSlicePromise = addSlices(this.dataValues, this.id, this.#KVIN)
          .then(() => {
            debugging('slice-upload') && console.info('970: slice data uploaded');
            if (this.autoClose === false)
            {
              debugging('slice-upload') && console.info('slice data uploaded to open job...');
              return;
            }

            debugging('slice-upload') && console.info('slice data uploaded, closing job...');
            return this.close();
          });
        }

        /* A default reference the Job is set, if we have not already unreffed the job. */
        if (this.#jobRef !== false)
          this.ref();
        
        // await all promises  for operations that can be done after the job is deployed
        await Promise.all([listenersPromise, computeGroupsPromise, uploadSlicePromise]);
        
        this.readyStateChange('deployed');
        this.emit('accepted', this);
      }
      catch (error)
      {
        if (ON_BROWSER)
          await ClientModal.alert(error, { title: 'Failed to deploy job!' });
        throw error;
      }
      finally
      {
        this.#jobDeployRef.unref();
      }
    }
    else
    {
      // reconnecting to an old job
      await this.addInitialEvents();
      this.readyStateChange('reconnected');
    }

    this.connected = true;

    return new Promise((resolve, reject) => {
      const onComplete = () => { resolve(this.results); };

      const onCancel = (event) => {
        /** @todo event should carry reason and job public details - put them in dialog */
        if (ON_BROWSER && !this.listeningForCancel)
          ClientModal.alert('This job has been cancelled', { title: 'Job Cancelled' });
        this.emit('cancel', event);

        let errorMsg =  event.reason;
        if (event.error && event.error !== 'undefined')
          errorMsg = errorMsg +`\n Recent error message: ${event.error.message}`

        reject(new DCPError(errorMsg, event.code));
      };

      this.ee.once('stopped', async (stopEvent) => {
        // There is a chance the result submitter will emit finished > 1 time. Only handle it once.
        if (this.receivedStop)
          return;
        this.receivedStop = true;
        this.emit('stopped', stopEvent.runStatus);
        switch (stopEvent.runStatus) {
          case jobStatus.finished:
            if (this.collateResults)
            {
              let allSliceNumbers = Array.from(Array(stopEvent.sliceCount)).map((e,i)=>i+1);
              let remainSliceNumbers = allSliceNumbers.filter((e) => !this.results.isAvailable(e - 1));

              if (remainSliceNumbers.length)
              {
                const promises = remainSliceNumbers.map(sliceNumber => this.results.fetch([sliceNumber], true));
                await Promise.all(promises);
              }
            }

            this.emit('complete', this.results);
            onComplete();
            break;
          case jobStatus.cancelled:
            onCancel(stopEvent);
            break;
          default:
            /**
             * Asserting that we should never be able to reach here. The only
             * scheduler events that should trigger the Job's 'stopped' event
             * are jobStatus.cancelled, jobStatus.finished, and jobStatus.paused.
             */
            reject(new Error(`Unknown event "${stopEvent.runStatus}" caused the job to be stopped.`));
            break;
        }
      });

    }).finally(() => {
      // Create an async IIFE to not block the promise chain
      (async () => {
        // delay to let last few events to be received
        await new Promise((resolve) => setTimeout(resolve, 1000));
        
        // close all of the connections so that we don't cause node processes to hang.
        this.closeDeployConnection();
        this.eventSubscriber.close();
      })();

      /* Remove the reference to the Job. */
      this.unref();
    });
  }

  /**
   * job.addListeners(): Private function used to set up event listeners to the scheduler
   *                           before deploying the job.
   */
  async addInitialEvents ()
  {
    this.readyStateChange('listeners');

    // This is important: We need to flush the task queue before adding listeners
    // because we queue pending listeners by listening to the newListener event (in the constructor).
    // If we don't flush here, then the newListener events may fire after this function has run,
    // and the events won't be properly set up.
    await new Promise(resolve => setTimeout(resolve, 0));

    // @todo: Listen for an estimated cost, probably emit an "estimated" event when it comes in?
    // also @todo: Do the estimation task(s) on the scheduler and send an "estimated" event

    // Always listen to the stop event. It will resolve the work function promise, so is always needed.
    this.on('stop', (ev) => {this.ee.emit('stopped', ev)});

    // Connect listeners that were set up before exec
    if (this.desiredEvents.includes('result'))
      this.listeningForResults = true;
    await this.subscribeNewEvents(this.desiredEvents);

    // Connect listeners that are set up after exec
    this.on('newListener', (evt) => {
      if (evt === 'newListener' || this.desiredEvents.includes(evt))
        return;
      this.subscribeNewEvents([evt]);
    });
    
    // automatically add a listener for results if collateResults is on
    if (this.collateResults && !this.listeningForResults)
      this.on('result', () => {});

    debugging('dcp-client') && console.debug('subscribedEvents', this.desiredEvents);

    // If we have listeners for job.work, subscribe to custom events
    if (this.listenForCustomEvents)
      await this.subscribeCustomEvents();
    // Connect work event listeners that are set up after exec
    else
      this.work.on('newListener', () => this.subscribeCustomEvents());
  }
  
  /**
   * Subscribes to either reliable events or optional events. It is assumed that
   * any call to this function will include only new events.
   * @param {string[]} events 
   */
  async subscribeNewEvents (events)
  {
    const reliableEvents = [];
    const optionalEvents = [];
    for (let eventName of events)
    {
      eventName = eventName.toLowerCase();
      if (this.eventTypes[eventName] && this.eventTypes[eventName].reliable)
        reliableEvents.push(eventName);
      else if (this.eventTypes[eventName] && !this.eventTypes[eventName].reliable)
        optionalEvents.push(eventName);
      else
        debugging('dcp-client') && console.debug(`Job handler has listener ${eventName} which isn't an event-router event.`);
    }
    if (debugging('dcp-client'))
    {
      console.debug('reliableEvents', reliableEvents);
      console.debug('optionalEvents', optionalEvents);
    }
    await this.eventSubscriber.subscribeManyEvents(reliableEvents, optionalEvents, { filter: { job: this.id } });
  }
  
  /**
   * Establishes listeners for worker events when requested by the client
   */
  async subscribeCustomEvents ()
  {
    if (!this.listeningForCustomEvents)
      await this.eventSubscriber.subscribeManyEvents([], ['custom'], { filter: { job: this.id } });
    this.listeningForCustomEvents = true
  }
  
  async joinComputeGroups ()
  {
    if (!Array.isArray(this.computeGroups))      
      throw new DCPError('Compute groups must be wrapped in an Array', 'DCPL-1101');

    // localExec jobs are not entered in any compute group.
    if (!this.inLocalExec && this.computeGroups && this.computeGroups.length > 0)
    {
      this.readyStateChange('compute-groups');

      // Add this job to its currently-defined compute groups (as well as public group, if included)
      let success;
      for (let i = 0; i < this.computeGroups.length; i++)
      {
        let value = this.computeGroups[i];
        
        if (typeof value !== 'object')
          throw new DCPError(`This compute group: ${value[i]} must be an object`, 'DCPL-1102');
          
        if (value.joinKey && typeof value.joinKey !== 'string' && !(value.joinKey instanceof String))
          throw new DCPError(`This join key: ${value.joinKey} must be a string or a string literal`, 'DCPL-1103');
        else if (value.joinKeystore && !(value.joinKeystore instanceof wallet.Keystore))
          throw new DCPError(`This join Keystore: ${value.joinKeystore} must be an instance of wallet.Keystore`, 'DCPL-1104');
        else if (!value.joinKey && !value.joinKeystore)
          throw new DCPError('Compute group must contain a joinKey or a joinKeystore', 'DCPL-1105');
      }
        
      try
      {
        const cgPayload = await computeGroups.addJobToGroups(this.id, this.computeGroups);
        success = true; // To support older version of CG service where addJobToGroups had void/undefined return.
        if (cgPayload) success = cgPayload.success;
        debugging('dcp-client') && console.debug('job/index: addJobToGroups cgPayload:', cgPayload ? cgPayload : 'cgPayload is not defined; probably from legacy CG service.');
      }
      catch (e)
      {
        debugging('dcp-client') && console.debug('job/index: addJobToGroups threw exception:', e);
        success = false;
      }

      /* Could not put the job in any compute group, even though the user wanted it to run. Cancel the job. */
      if (!success)
      {
        await this.cancel('compute-groups::Unable to join any compute groups');
        throw new DCPError(`Access Denied::Failed to add job ${this.id} to any of the desired compute groups on ${this.scheduler}`, 'DCPL-1100');
      }
    }
  }
  
  /**
   * Takes result events as input, stores the result and fires off
   * events on the job handle as required. (result, duplicate-result)
   *
   * @param {object} ev - the event recieved from protocol.listen('/results/0xThisGenAdr')
   */
  async handleResult (ev)
  {
    if (this.results === null)
      // This should never happen - the onResult event should only be established/called
      // in addListeners which should also initialize the internal results array
      throw new Error('Job.onResult was invoked before initializing internal results');
    
    const { result: _result } = ev.result;
    debugging('dcp-client') && console.debug('handleResult', _result);
    /**
     * There is currently not way to access OAM instance on the job level.
     * This case is similar to the fetch case in result-handle.js.
     * The value to fetchURI is either a dataURI, RDS that user defined or scheduler disk location.
     */
    let result = (_result === skipStorage) ? _result : await fetchURI(_result, false);
    if (this.results.isAvailable(ev.sliceNumber - 1))
    {
      const changed = JSON.stringify(this.results[ev.sliceNumber - 1]) !== JSON.stringify(result);
      this.emit('duplicate-result', { sliceNumber: ev.sliceNumber, changed });
    }

    this.results.newResult(result, ev.sliceNumber - 1);
  }
  
  /**
   * Receives status events from the scheduler, updates the local status object
   * and emits a 'status' event
   *
   * @param {object} ev - the status event received from
   * protocol.listen('/status/0xThisGenAdr')
   * @param {boolean} emitStatus - value indicating whether or not the status
   * event should be emitted
   */
  handleStatus ({ runStatus, total, distributed, computed }, emitStatus = true)
  {
    Object.assign(this.status, {
      runStatus,
      total,
      distributed,
      computed,
    });

    if (emitStatus)
      this.emit('status', { ...this.status, job: this.id });
  }
  
  /**
   * Sends a request to the scheduler to deploy the job.
   */
  async deployJob ()
  {
    var moduleDependencies;    
    
    // Set a reference to hold the client app open while the job is deploying...
    this.#jobDeployRef.ref();
    
    /* Send sideloader bundle to the package server */
    if (dcpEnv.platform === 'nodejs' && this.dependencies.length)
    {
      let { pkg, unresolved } = await this._publishLocalModules();

      moduleDependencies = unresolved;
      if (pkg)
        moduleDependencies.push(pkg.name + '/' + sideloaderModuleIdentifier);  
    }
    else
      moduleDependencies = this.dependencies;
    
    this.readyStateChange('preauth');

    const adhocId = this.id.slice(this.id.length - 6, this.id.length);
    const { dataRange, dataValues, dataPattern, sliceCount } = marshalInputData(this.jobInputData);
    if(dataValues)
      this.dataValues = dataValues;

    // Set worktime information
    if (!this.worktime)
    {
      this.worktime = 'map-basic';
      this.worktimeVersion = '^1.0.0';
    }
    var worktimes;
    if (!dcpEnv.isBrowserPlatform)
    {
      worktimes = requireNative('./generated/worktimes');
      if (!Array.isArray(worktimes))
        worktimes = Array.from(worktimes); // handle ctx-module converting into an array-like object
    }
    else
    {
      const worktimesLocation = new URL(dcpConfig.scheduler.location);
      worktimesLocation.pathname = '/dcp-client/generated/worktimes.json';
      const response = await fetch(worktimesLocation);
      worktimes = await response.json();
    }
    const thisWorktime = worktimes.find((wt) => wt.name === this.worktime);
    if (thisWorktime)
    {
      if (!this.worktimeVersion)
        this.worktimeVersion = '^' + thisWorktime.version;
    }
    else
    {
      if (!this.customWorktime)
        throw new DCPError(`Invalid Worktime ${this.worktime}`, 'ENOWORKTIME');
      if (!this.worktimeVersion)
        this.worktimeVersion = '>=0.0.0';
    }

    this.readyStateChange('deploying');

    let requirementBits = capabilities.toBits(this.requirements);
    // Make all jobs deployed post KVINv9 update set that as a required capability
    requirementBits += schedulerConstants.capabilities.KVINv9;

    /* Payload format is documented in scheduler-v4/libexec/job-submit/operations/submit.js */
    const submitPayload = {
      paymentAccount:           this.paymentAccountKeystore.address,

      workFunctionURI:          this.workFunctionURI,
      job:                      this.id,
      mvMultSlicePayment:       Number(this.feeStructure.marketValue) || 0,      // @todo: improve feeStructure internals to better reflect v4
      absoluteSlicePayment:     Number(this.feeStructure.maxPerRequest) || 0,
      requirePath:              this.requirePath,
      dependencies:             moduleDependencies,
      requirements:             requirementBits, /* capex */
      localExec:                this.inLocalExec,
      discrete:                 this.requirements.discrete,
      useStrict:                this.requirements.useStrict,
      estimationSlices:         this.estimationSlices,
      greedyEstimation:         this.greedyEstimation,
      workerConsole:            this.workerConsole,
      isOpen:                   !this.autoClose,

      description:              this.public.description || 'Discreetly making the world smarter',
      name:                     this.public.name        || 'Ad-Hoc Job' + adhocId,
      link:                     this.public.link        || '',

      resultStorageType:        this.resultStorageType,    // @todo: implement other result types
      resultStorageDetails:     this.resultStorageDetails, // Content depends on resultStorageType
      resultStorageParams:      this.resultStorageParams, // Post params for off-prem storage
      dataRange,
      dataPattern,
      sliceCount,
      marshaledDataValues:     this.marshaledDataValues,
      rangeLength:             this.rangeLength,
      worktime:                { name: this.worktime, version: this.worktimeVersion, custom: this.customWorktime },
    };

    // Check if dataRange or dataPattern input is already marshaled
    if (this.marshaledDataRange)
      submitPayload.dataRange = this.marshaledDataRange;

    /* Determine composition of argument set and build payload */
    if (this.marshaledArguments)
      submitPayload.marshaledArguments = this.marshaledArguments;
    else if (this.jobArguments)
    {
      if (this.jobArguments instanceof RemoteDataPattern) /* Not supported */
        throw new DCPError('Cannot use RemoteDataPattern as work function arguments', 'EBADARG')
      if (this.jobArguments instanceof RemoteDataSet) /* Entire set is RemoteDataSet */
        this.jobArguments = this.jobArguments.map((e) => new URL(e));

      submitPayload.marshaledArguments = this.#KVIN.marshal(encodeJobValueList(this.jobArguments, 'jobArguments', this.#KVIN));
    }

    // XXXpfr Excellent tracing.
    if (debugging('dcp-client'))
    {
      const { dumpObject } = require('dcp/utils');
      dumpObject(submitPayload, 'Submit: Job Index: submitPayload', 256);
    }
    debugging('dcp-client') && console.debug('submitPayload.marshaledArguments', JSON.stringify(submitPayload.marshaledArguments).length, JSON.stringify(submitPayload).length);

    // Deploy the job! If we get an error, try again a few times until threshold of errors is reached, then actually throw it
    let deployed
    let deployAttempts = 0;
    while (deployAttempts++ < (dcpConfig.job.deployAttempts || 10))
    {
      try
      {
        deployed = await this.useDeployConnection('submit@5', submitPayload, undefined, [this.paymentAccountKeystore]);
        break;
      }
      catch (e)
      {
        if (deployAttempts < 10)
          debugging('dcp-client') && console.debug('Error when trying to deploy job, trying again', e);
        else
          throw e;
      }
    }

    if (!deployed.success)
    {
      // close all of the connections so that we don't cause node processes to hang.
      this.closeDeployConnection();
      this.eventSubscriber.close();
      
      // Yes, it is possible for deployed or deployed.payload to be undefined.
      if (deployed && deployed.payload)
      {
        throw new DCPError('Failed to submit job:' + deployed.payload?.message, deployed.payload.code);
      }
      throw new DCPError(`Failed to submit job (no payload; ${deployed || typeof deployed})`);
    }

    debugging('dcp-client') && console.debug('After Deploy', deployed);

    this.address = deployed.payload.escrow;

    if (!this.status)
      this.status = {
        runStatus: null,
        total: 0,
        computed: 0,
        distributed: 0,
      };
    
    this.status.runStatus = deployed.payload.status;
    this.status.total = deployed.payload.lastSliceNumber;
    this.running = true;
  }
  
  /** close an open job to indicate we are done adding data so it is okay to finish
   *  the job at the appropriate time
   */
  close ()
  {
    // Don't make pointless extra calls to closeJobs - just return the promise
    if (this.closePromise)
      return this.closePromise;

    const resultSubmitterConnection = new protocolV4.Connection(dcpConfig.scheduler.services.resultSubmitter);
    this.closePromise = resultSubmitterConnection.request('closeJob', {
      job: this.id,
    });
    this.closePromise.finally(() => resultSubmitterConnection.close());
  
    return this.closePromise;
  }
  
  /** Use the connection to job submit service. Will open a new connection if one does not exist,
   *  and close the connection if it is idle for more than 10 seconds (tuneable).
   */
  async useDeployConnection(operation, payload, identity, signKeystores)
  {
    if (!this.useDeployConnection.uses)
      this.useDeployConnection.uses = 0;
    this.useDeployConnection.uses++;
    if (!this.deployConnection)
    {
      debugging('deploy-connection') && console.info(`1453: making a new deployConnection...`)
      this.deployConnection = new protocolV4.Connection(dcpConfig.scheduler.services.jobSubmit, { identity }); 
      this.deployConnection.on('end', () => { this.deployConnection = null; });
    }
    if (this.deployConnectionTimeout)
      clearTimeout(this.deployConnectionTimeout);

    debugging('deploy-connection') && console.info(`1460: sending ${operation} request...`);
    const deployRequest = new this.deployConnection.Request(operation, payload);
    if (signKeystores)
      for (let keystore of signKeystores)
        await deployRequest.authorize(keystore);

    const deployPromise = deployRequest.send();

    const deployChain = deployPromise.finally(() => {
      this.useDeployConnection.uses--;

      debugging('deploy-connection') && console.info(`1462: deployConnection done ${operation} request, connection uses is ${this.useDeployConnection.uses}`)

      this.deployConnectionTimeout = setTimeout(() => {
        if (this.useDeployConnection.uses === 0 && this.deployConnection)
        {
          debugging('deploy-connection') && console.info(`1469: done with deployConn, closing...`);
          // if we're done w/ the connection, then remove its cleanup
          // function, close it, and clean up manually to make room for a
          // new conn when needed (ie, don't leave the closing conn where
          // someone could accidentally pick it up)
          this.deployConnection.removeAllListeners('end');
          this.deployConnection.close();
          this.deployConnection = null;
        }
      }, (dcpConfig.job.deployCloseTimeout || 10 * 1000));
      this.deployConnectionTimeout.unref();
    }); 
    
    return deployChain;
  }
  
  /**
   *  Close the connection to the job submit (if it exists), and clear the close timeout (if needed).
   */
  closeDeployConnection()
  {
    if (this.deployConnection)
      this.deployConnection.close();
    this.deployConnection = null;
    if (this.deployConnectionTimeout)
      clearTimeout(this.deployConnectionTimeout);
    this.deployConnectionTimeout = null;
  }

  unref()
  {
    if (this.#jobRef)
      this.#jobRef.unref();
    else
      this.#jobRef = false;

    this.#jobDeployRef.unref();
  }

  ref()
  {
    if (!this.#jobRef)
      this.#jobRef = new Reference(debugging, ()=>`Job reference:  ${this.id || '(undeployed)'}`);           /* Keep the job alive. */
    this.#jobRef.ref();
  }
}

/** 
 * Encode a value list for transmission to the job-submit daemon. This could be either job arguments
 * or the slice input set, if the input set was an Array-like object.
 *
 * @param {{ length: number }} valueList - The list of values to encode
 * @returns {Array<URL|{string: string, method:string, MIMEType: string }>} - URL or object with 3 properties:
 *             string   - serialization of value
 *             method   - how value was serialized (e.g. 'kvin', 'json')
 *             MIMEType - MIME information corresponding to method (e.g. 'application/kvin', 'application/json')
 */
function encodeJobValueList(valueList, valueKind, serializer)
{
  var list = [];

 /*
  * We need to handle several different styles of datasets, and create the output array accordingly.
  *
  * 1. instance of RemoteDataSet or RemoteDataPattern => apply new URI(?)
  * 2. an Array-like objects     => apply serializeJobValue
  * Values sent to the scheduler in payload are either URL or the result of calling serializeJobValue.
  */

  if (typeof valueList === 'undefined' || (typeof valueList === 'object' && valueList.length === 0))
    return list; /* empty set */

  if (typeof valueList !== 'object' || !valueList.hasOwnProperty('length'))
    throw new Error('value list must be an Array-like object');

  for (let i = 0; i < valueList.length; i++) /* Set is composed of values from potentially varying sources */
  {
    let value = valueList[i];
    if (value instanceof RemoteDataSet)
      value.forEach((el) => list.push(new URL(el)));
    else if (value instanceof RemoteDataPattern)
    {
      if (valueKind === jobValueKind.jobArguments)
        throw new DCPError('Cannot use RemoteDataPattern as work function arguments', 'EBADARG');
      else
      {
        let uri = valueList['pattern'];
        for (let sliceNum = 1; sliceNum <= valueList['sliceCount']; sliceNum++)
          list.push(new URL(uri.replace('{slice}', sliceNum)))
      }
    }
    else if (value instanceof RemoteValue)
      list.push(serializeJobValue(value.href, serializer));
    else
      list.push(serializeJobValue(value, serializer));
  }

  return list;
} 

/**
 * Depending on the shape of the job's data, resolve it into a RangeObject, a
 * Pattern, or a values array, and return it in the appropriate property.
 *
 * @param  {any}    data Job's input data
 * @return {MarshaledInputData} An object with one of the following properties set:
 * - dataValues: job input is an array of arbitrary values 
 * - dataPattern: job input is a URI pattern 
 * - dataRange: job input is a RangeObject (and/or friends)
 */
function marshalInputData (data)
{
  if (!(data instanceof Object || data instanceof SuperRangeObject))
    throw new TypeError(`Invalid job data type: ${typeof data}`);

  const marshalledInputData = {};

  // TODO(wesgarland): Make this more robust.
  if (data instanceof SuperRangeObject ||
      (data.hasOwnProperty('ranges') && data.ranges instanceof MultiRangeObject) ||
      (data.hasOwnProperty('start') && data.hasOwnProperty('end')))
    marshalledInputData.dataRange = data;
  else if (Array.isArray(data))
    marshalledInputData.dataValues = data;
  else if (data instanceof URL || data instanceof DcpURL)
    marshalledInputData.dataPattern = String(data);
  else if(data instanceof RemoteDataSet)
    marshalledInputData.dataValues = data.map(e => new URL(e));
  else if(data instanceof RemoteDataPattern)
  {
    marshalledInputData.dataPattern = data['pattern'];
    marshalledInputData.sliceCount = data['sliceCount'];
  }

  debugging('job') && console.debug('marshalledInputData:', marshalledInputData);
  return marshalledInputData;
}

exports.Job = Job;
exports.SlicePaymentOffer = SlicePaymentOffer;
exports.ResultHandle = ResultHandle;
exports.addSlices = addSlices;
exports.fetchResults = fetchResults;
